import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';

type AdminAccess = 'checking' | 'allowed' | 'denied';

/**
 * Fail-closed admin guard.
 *
 * Children are NEVER rendered until the admin role has been positively
 * confirmed server-side (SECURITY DEFINER `has_role`). Any error, timeout,
 * missing session or unexpected payload results in denial.
 */
export default function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const [access, setAccess] = useState<AdminAccess>('checking');

  useEffect(() => {
    let mounted = true;

    if (authLoading) {
      setAccess('checking');
      return () => {
        mounted = false;
      };
    }

    if (!user) {
      setAccess('denied');
      return () => {
        mounted = false;
      };
    }

    setAccess('checking');

    (async () => {
      let allowed = false;
      try {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin',
        });
        allowed = !error && data === true;
      } catch {
        allowed = false;
      }
      if (!mounted) return;
      setAccess(allowed ? 'allowed' : 'denied');
    })();

    return () => {
      mounted = false;
    };
  }, [authLoading, user]);

  if (authLoading || access === 'checking') {
    return <div role="status" aria-label="Checking admin access" aria-busy="true" style={{ padding: 16 }}>Loading…</div>;
  }

  if (access === 'denied') {
    if (!user) {
      const next = `${location.pathname}${location.search}${location.hash}`;
      return <Navigate to={`/auth/signin?next=${encodeURIComponent(next)}`} replace />;
    }
    return <Navigate to="/settings/billing" replace />;
  }

  return <>{children}</>;
}
